import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const KEY = "esc_cookie_consent";

// Configurable cookie banner. No marketing tracking is loaded before consent.
export function CookieBanner() {
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) {
        const timer = setTimeout(() => setVisible(true), 1200);
        return () => clearTimeout(timer);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const decide = (value) => {
    try {
      localStorage.setItem(KEY, value);
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  const txt = lang === "en"
    ? {
        text: "We use only technical cookies necessary for the site to work. No marketing tracking is active without your consent.",
        accept: "Accept", reject: "Only necessary", more: "Cookie Policy",
      }
    : {
        text: "Utilizziamo solo cookie tecnici necessari al funzionamento del sito. Nessun tracciamento marketing è attivo senza il tuo consenso.",
        accept: "Accetta", reject: "Solo necessari", more: "Cookie Policy",
      };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4 }}
          data-testid="cookie-banner"
          className="fixed inset-x-3 bottom-20 z-[60] mx-auto max-w-2xl rounded-xl border border-edge bg-asphalt/95 p-5 shadow-2xl backdrop-blur-md lg:bottom-4"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Cookie className="hidden h-8 w-8 shrink-0 text-amber sm:block" />
            <p className="flex-1 text-sm leading-relaxed text-steel">
              {txt.text}{" "}
              <Link to="/cookie" className="font-semibold text-amber hover:underline">{txt.more}</Link>
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={() => decide("necessary")} data-testid="cookie-reject" className="rounded-md border border-edge px-4 py-2 text-xs font-bold uppercase text-ice hover:border-steel">
                {txt.reject}
              </button>
              <button type="button" onClick={() => decide("all")} data-testid="cookie-accept" className="rounded-md bg-amber px-4 py-2 text-xs font-bold uppercase text-carbon hover:bg-amber/90">
                {txt.accept}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
